import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, ChevronDown, Folder, FolderOpen, Loader2 } from 'lucide-react'
import { FolderTreeNode, StorageUsage, DragDropHandlers } from '@features/file-explorer/types'
import { useFolderDragHandlers } from '@features/file-operations/hooks/useFolderDragHandlers'
import StorageUsageBar from './StorageUsageBar'

interface FolderTreeProps {
  tree: FolderTreeNode[]
  currentFolderId: string | null
  onNavigate: (folderId: string | null) => void
  loadingFolderId: string | null
  dragAndDrop: DragDropHandlers
  storageUsage?: StorageUsage | null
}

interface TreeNodeProps {
  node: FolderTreeNode
  currentFolderId: string | null
  level: number
  expandedFolders: Set<string>
  onToggleExpand: (folderId: string) => void
  onNavigate: (folderId: string | null) => void
  loadingFolderId: string | null
  dragAndDrop: DragDropHandlers
}

function TreeNode({ node, currentFolderId, level, expandedFolders, onToggleExpand, onNavigate, loadingFolderId, dragAndDrop }: TreeNodeProps) {
  const isExpanded = expandedFolders.has(node.id)
  const isCurrent = currentFolderId === node.id
  const isLoading = loadingFolderId === node.id
  const hasChildren = node.children && node.children.length > 0

  const handleFolderClick = () => {
    if (!isLoading) {
      onNavigate(node.id)
    }
  }

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasChildren) {
      onToggleExpand(node.id)
    }
  }

  // Drag and drop handlers
  const folderDragHandlers = useFolderDragHandlers({
    folderId: node.id,
    dragDropHandlers: dragAndDrop,
    targetPrefix: 'tree-folder'
  })

  return (
    <div>
      <div
        className={`flex items-center gap-1 py-1.5 px-2 rounded transition-colors ${
          isCurrent ? 'bg-primary/10 text-primary font-medium' : ''
        } ${folderDragHandlers.isDragOverThisFolder ? 'bg-primary/20 ring-2 ring-primary' : ''} ${isLoading ? 'pointer-events-none' : 'cursor-pointer hover:bg-base-200'}`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
        onClick={handleFolderClick}
        onDragEnter={folderDragHandlers.handleDragEnter}
        onDragOver={folderDragHandlers.handleDragOver}
        onDragLeave={folderDragHandlers.handleDragLeave}
        onDrop={folderDragHandlers.handleDrop}
        data-testid={`folder-tree-item-${node.id}`}
      >
        {/* Expand/Collapse Icon */}
        <button
          onClick={handleToggleClick}
          className="p-0.5 hover:bg-base-300 rounded transition-colors"
          disabled={!hasChildren}
          data-testid={`folder-tree-toggle-${node.id}`}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>

        {/* Folder Icon */}
        {isLoading ? (
          <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
        ) : isExpanded && hasChildren ? (
          <FolderOpen className="w-4 h-4 flex-shrink-0" />
        ) : (
          <Folder className="w-4 h-4 flex-shrink-0" />
        )}

        {/* Folder Name */}
        <span className="text-sm truncate flex-1">{node.name}</span>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              currentFolderId={currentFolderId}
              level={level + 1}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
              onNavigate={onNavigate}
              loadingFolderId={loadingFolderId}
              dragAndDrop={dragAndDrop}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function FolderTree({ tree, currentFolderId, onNavigate, loadingFolderId, dragAndDrop, storageUsage }: FolderTreeProps) {
  const { t } = useTranslation()
  // '__root__' is used as a sentinel value to indicate loading root (null folder)
  const isRootLoading = loadingFolderId === '__root__'

  // Drag and drop handlers for root folder
  const rootDragHandlers = useFolderDragHandlers({
    folderId: null,
    dragDropHandlers: dragAndDrop,
    targetPrefix: 'tree-folder'
  })

  // Initialize expanded folders - expand the current folder AND its ancestors
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => {
    const expanded = new Set<string>()

    // Function to find and expand ancestors of current folder
    const findAndExpandAncestors = (nodes: FolderTreeNode[], targetId: string | null): boolean => {
      if (targetId === null) return false

      for (const node of nodes) {
        if (node.id === targetId) {
          // Expand the current folder itself so we can see its children
          expanded.add(node.id)
          return true
        }
        if (node.children && node.children.length > 0) {
          if (findAndExpandAncestors(node.children, targetId)) {
            // Expand parent folders so we can see the path to current folder
            expanded.add(node.id)
            return true
          }
        }
      }
      return false
    }

    findAndExpandAncestors(tree, currentFolderId)

    return expanded
  })

  const handleToggleExpand = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  // Handler for root folder click
  const handleRootClick = () => {
    if (!isRootLoading) {
      onNavigate(null)
    }
  }

  return (
    <div className="h-full flex flex-col bg-base-100">
      {/* Storage Usage Bar */}
      {storageUsage && <StorageUsageBar storage={storageUsage} />}
      <div className="p-3 border-t border-base-300">
        <h3 className="text-sm font-semibold text-base-content/70">{t('fileExplorer.folderTree.title')}</h3>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {/* Root folder */}
        <div
          className={`flex items-center gap-1 py-1.5 px-2 rounded transition-colors ${
            currentFolderId === null ? 'bg-primary/10 text-primary font-medium' : ''
          } ${rootDragHandlers.isDragOverThisFolder ? 'bg-primary/20 ring-2 ring-primary' : ''} ${isRootLoading ? 'pointer-events-none' : 'cursor-pointer hover:bg-base-200'}`}
          onClick={handleRootClick}
          onDragEnter={rootDragHandlers.handleDragEnter}
          onDragOver={rootDragHandlers.handleDragOver}
          onDragLeave={rootDragHandlers.handleDragLeave}
          onDrop={rootDragHandlers.handleDrop}
          data-testid="folder-tree-root"
        >
          <div className="w-4 h-4" />
          {isRootLoading ? (
            <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
          ) : (
            <Folder className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="text-sm truncate flex-1">{t('fileExplorer.folderTree.root')}</span>
        </div>

        {/* Tree nodes */}
        {tree.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            currentFolderId={currentFolderId}
            level={0}
            expandedFolders={expandedFolders}
            onToggleExpand={handleToggleExpand}
            onNavigate={onNavigate}
            loadingFolderId={loadingFolderId}
            dragAndDrop={dragAndDrop}
          />
        ))}
      </div>
    </div>
  )
}
